import * as React from "react"

import { Input } from "../Input/Input"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverItem,
} from "../Popover/Popover"

export interface SuggestionInputProps {
  id?: string
  "aria-label": string
  value: string
  onValueChange: (value: string) => void
  options: string[]
  placeholder?: string
}

export function SuggestionInput({
  id,
  "aria-label": label,
  value,
  onValueChange,
  options,
  placeholder,
}: SuggestionInputProps) {
  const listId = React.useId()
  const inputRef = React.useRef<HTMLInputElement>(null)
  const [open, setOpen] = React.useState(false)
  const [active, setActive] = React.useState(-1)
  const filtered = React.useMemo(
    () =>
      options.filter((option) =>
        option.toLowerCase().includes(value.toLowerCase()),
      ),
    [options, value],
  )
  // Keep the highlighted option in range as the list narrows or refreshes.
  React.useEffect(() => {
    setActive((current) => (current >= filtered.length ? -1 : current))
  }, [filtered.length])

  const select = (option: string) => {
    onValueChange(option)
    inputRef.current?.focus()
    setOpen(false)
    setActive(-1)
  }

  React.useEffect(() => {
    if (open && active >= 0) {
      document
        .getElementById(`${listId}-${active}`)
        ?.scrollIntoView({ block: "nearest" })
    }
  }, [active, listId, open])

  return (
    <Popover open={open && filtered.length > 0} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div>
          <Input
            ref={inputRef}
            id={id}
            role="combobox"
            aria-label={label}
            aria-autocomplete="list"
            aria-expanded={open && filtered.length > 0}
            aria-controls={listId}
            aria-activedescendant={
              open && active >= 0 && active < filtered.length
                ? `${listId}-${active}`
                : undefined
            }
            autoComplete="off"
            placeholder={placeholder}
            value={value}
            onFocus={() => setOpen(true)}
            onClick={() => setOpen(true)}
            onChange={(event) => {
              onValueChange(event.target.value)
              setActive(-1)
              setOpen(true)
            }}
            onKeyDown={(event) => {
              if (
                (event.key === "ArrowDown" || event.key === "ArrowUp") &&
                filtered.length
              ) {
                event.preventDefault()
                setOpen(true)
                setActive((current) =>
                  event.key === "ArrowDown"
                    ? Math.min(current + 1, filtered.length - 1)
                    : Math.max(current - 1, 0),
                )
              } else if (
                event.key === "Enter" &&
                open &&
                active >= 0 &&
                filtered[active]
              ) {
                event.preventDefault()
                select(filtered[active])
              } else if (event.key === "Escape" && open) {
                event.preventDefault()
                event.stopPropagation()
                setOpen(false)
              } else if (event.key === "Tab") {
                setOpen(false)
              }
            }}
          />
        </div>
      </PopoverAnchor>
      <PopoverContent
        className="p-1 w-[var(--radix-popover-trigger-width)] max-h-60 overflow-y-auto"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => event.preventDefault()}
        onInteractOutside={(event) => {
          if (event.target === inputRef.current) {
            event.preventDefault()
          }
        }}
      >
        <div id={listId} role="listbox" aria-label={`${label} suggestions`}>
          {filtered.map((option, index) => (
            <PopoverItem
              id={`${listId}-${index}`}
              key={option}
              role="option"
              aria-selected={index === active}
              tabIndex={-1}
              className={index === active ? "bg-surface-contrast" : undefined}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => select(option)}
            >
              {option}
            </PopoverItem>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
